<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaCheckMenu, {
  type GaiaCheckMenuOption,
} from '@/components/design/composite/GaiaCheckMenu.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import { IconEdit, IconShare } from '@/components/design/icons';
import MassShareContractsModal from '@/components/contractManagement/MassShareContractsModal.vue';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import { ACCESS_LEVELS, formatAccessPermissions } from '@/utils/accessPermission';
import { initialsOf } from '@/utils/initials';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Who a contractual party is shared with, and the Share dialog — the party
 * page's version of the contract page's shared users / roles panel. Only
 * explicit shares are listed: the creator, org admins, roles with access to
 * all parties and access through a linked contract never appear here.
 * Sharing, changing or removing a share needs ADMIN on the party: the parent
 * passes why the caller lacks it, and every control then stays visible but
 * disabled with that reason.
 */
const props = withDefaults(
  defineProps<{ partyUid: string; partyName: string; blockedReason?: string | null }>(),
  { blockedReason: null }
);
const emit = defineEmits<{ count: [value: number] }>();

const message = useGaiaToast();

interface ShareRow {
  key: string;
  kind: 'user' | 'role';
  uid: string;
  name: string;
  meta: string;
  permissions: number;
}

const rows = ref<ShareRow[]>([]);
const loading = ref(false);
const removingKey = ref<string | null>(null);
const showShareModal = ref(false);

async function load(): Promise<void> {
  loading.value = true;
  try {
    const [users, roles] = await Promise.all([
      ContractualPartyShareService.getUserSharesByParty(props.partyUid),
      ContractualPartyShareService.getRoleSharesByParty(props.partyUid),
    ]);
    rows.value = [
      ...users.map(share => ({
        key: `user-${share.user.uid}`,
        kind: 'user' as const,
        uid: share.user.uid,
        name: share.user.full_name || share.user.email,
        meta: share.user.email,
        permissions: share.permissions,
      })),
      ...roles.map(share => ({
        key: `role-${share.role.uid}`,
        kind: 'role' as const,
        uid: share.role.uid,
        name: share.role.name,
        meta: 'Role',
        permissions: share.permissions,
      })),
    ];
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load shares');
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

async function remove(row: ShareRow): Promise<void> {
  if (removingKey.value) return;
  removingKey.value = row.key;
  try {
    await (row.kind === 'user'
      ? ContractualPartyShareService.removeUserShare(props.partyUid, row.uid)
      : ContractualPartyShareService.removeRoleShare(props.partyUid, row.uid));
    message.success(`${row.name} no longer has shared access`);
    await load();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to remove share');
  } finally {
    removingKey.value = null;
  }
}

// ── Change one share's access (the contract page's popover) ─────────────────

const editingKey = ref<string | null>(null);
const editBitmask = ref(0);
const savingEdit = ref(false);

function openEdit(row: ShareRow): void {
  editingKey.value = row.key;
  editBitmask.value = row.permissions;
}

const editOptions = computed<GaiaCheckMenuOption[]>(() =>
  ACCESS_LEVELS.map(level => ({
    key: String(level.value),
    label: level.label,
    checked: editBitmask.value === level.value,
  }))
);

async function saveEdit(row: ShareRow): Promise<void> {
  if (savingEdit.value) return;
  if (editBitmask.value === 0) {
    message.warning('Choose an access level');
    return;
  }
  savingEdit.value = true;
  try {
    await (row.kind === 'user'
      ? ContractualPartyShareService.updateUserSharePermissions(
          props.partyUid,
          row.uid,
          editBitmask.value
        )
      : ContractualPartyShareService.updateRoleSharePermissions(
          props.partyUid,
          row.uid,
          editBitmask.value
        ));
    message.success('Access updated');
    editingKey.value = null;
    await load();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update permissions');
  } finally {
    savingEdit.value = false;
  }
}

const partyUids = computed(() => [props.partyUid]);

watch(() => props.partyUid, load, { immediate: true });
watch(
  () => rows.value.length,
  count => {
    emit('count', count);
  }
);
</script>

<template>
  <n-flex vertical :size="12">
    <n-flex align="center" :size="8" :wrap="false">
      <gaia-text size="control" tone="subtle">
        Users and roles this party is shared with directly. Org admins and anyone reaching it
        through a contract or a role with access to all parties can open it too.
      </gaia-text>
      <gaia-button
        type="primary"
        size="medium"
        :style="{ marginLeft: 'auto' }"
        :disabled="props.blockedReason !== null"
        :title="props.blockedReason ?? undefined"
        @click="showShareModal = true"
      >
        <template #icon><n-icon :component="IconShare" /></template>
        Share
      </gaia-button>
    </n-flex>

    <gaia-table-skeleton
      v-if="loading && rows.length === 0"
      :row-style="{ gridTemplateColumns: 'minmax(0, 1fr) 120px' }"
      :cells="['46%', '96px']"
      :rows="3"
      :row-height="52"
      :action="false"
    />
    <gaia-empty-state v-else-if="rows.length === 0" title="Not shared with anyone yet" dashed>
      <template #icon><n-icon :component="IconShare" :size="18" /></template>
      Share this party to give users or roles access beyond the contracts it appears in.
    </gaia-empty-state>
    <n-flex v-else vertical :size="0">
      <n-flex
        v-for="row in rows"
        :key="row.key"
        align="center"
        :size="10"
        :wrap="false"
        :style="{ padding: '10px 0' }"
      >
        <gaia-avatar :size="26" :type="row.kind === 'role' ? 'mark' : undefined">
          {{ initialsOf(row.name) }}
        </gaia-avatar>
        <n-flex vertical :size="0" :style="{ minWidth: 0, flex: 1 }">
          <gaia-text truncate size="control" weight="semibold" tone="ink">
            {{ row.name }}
          </gaia-text>
          <gaia-text truncate size="meta" tone="subtle">{{ row.meta }}</gaia-text>
        </n-flex>
        <gaia-tag variant="muted">{{ formatAccessPermissions(row.permissions) }}</gaia-tag>
        <gaia-popover
          :show="editingKey === row.key"
          trigger="click"
          placement="bottom-end"
          @update:show="(value: boolean) => !value && (editingKey = null)"
        >
          <template #trigger>
            <gaia-icon-button
              :dimension="24"
              quaternary
              :title="props.blockedReason ?? 'Change access'"
              :disabled="props.blockedReason !== null"
              @click.stop="openEdit(row)"
            >
              <n-icon :component="IconEdit" :size="12" />
            </gaia-icon-button>
          </template>
          <gaia-check-menu
            title="Access"
            :options="editOptions"
            :loading="savingEdit"
            @toggle="(key: string) => (editBitmask = Number(key))"
            @cancel="editingKey = null"
            @confirm="saveEdit(row)"
          />
        </gaia-popover>
        <gaia-delete-button
          intent="remove"
          size="xs"
          subject="access"
          :disabled="props.blockedReason !== null"
          :title="props.blockedReason ?? undefined"
          :loading="removingKey === row.key"
          @click.stop="remove(row)"
        />
      </n-flex>
    </n-flex>

    <mass-share-contracts-modal
      v-model:show="showShareModal"
      kind="party"
      :contract-uids="partyUids"
      :contract-names="[partyName]"
      @shared="load"
    />
  </n-flex>
</template>
